<template>
    <div class="app__page-wrapper | u-padding-narrow" dusk="partner-order-overview-component">
        <form @submit.prevent="submitOrder">
            <div class="u-margin-bottom"></div>
            <div class="layout layout--center | u-full-height">
                <div class="layout__item u-4/5-desk">
                    <wizard-steps :step="4" />

                    <order-overview :customers="customerValues" :mollieUrl="mollieUrl" @setCustomer="setCustomer" />
                </div>
            </div>

            <wizard-footer>
                <div class="u-flex-between">
                    <customer-router-link :to="{ name: 'orderFullSsl' }">
                        <div class="button-back-wrapper">
                            <button type="button" class="btn btn--back">
                                {{ trans('order.navigation.ssl') }}
                            </button>
                        </div>
                    </customer-router-link>
                    <button
                        v-if="currentCustomer"
                        :disabled="loading || !hasProducts || !currentCustomer.isValidated"
                        type="submit"
                        class="btn btn--blue btn--nav"
                    >
                        {{ trans('order.order') }}
                    </button>
                </div>
            </wizard-footer>
        </form>
    </div>
</template>

<script>
import { apiMixin } from 'relapp-api';
import { pageLoadingStatusMixin } from 'relapp-loading';
import { mapActions, mapGetters } from 'vuex';

import WizardSteps from '@/components/WizardSteps.vue';
import WizardFooter from '@/components/WizardFooter.vue';
import {
    OrderOverview,
    trackingMixin,
    PaymentApi,
    supportMixin,
    orderPageMixin,
    paymentMixin,
    validationMixin
} from 'relapp-register';
import { ServersApi } from 'relapp-servers';
import { customerMixin } from 'relapp-customers';
import { EventBus } from 'relapp-event';
import { UsersApi } from 'relapp-users';

export default {
    components: {
        WizardSteps,
        WizardFooter,
        OrderOverview
    },
    mixins: [
        apiMixin,
        pageLoadingStatusMixin,
        trackingMixin,
        customerMixin,
        supportMixin,
        orderPageMixin,
        paymentMixin,
        validationMixin
    ],
    /**
     * Add all the properties found in the data object to Vue’s reactivity system.
     *
     * @return {object}
     */
    data() {
        return {
            mollieUrl: null
        };
    },
    props: {},
    metaInfo: {
        title: trans('page.order.overview')
    },
    computed: {
        ...mapGetters({
            hasProducts: 'order/hasProducts',
            user: 'user/user',
            currentCustomer: 'user/currentCustomer',
            order: 'order/order'
        })
    },
    /**
     * Called after the instance has been mounted.
     */
    mounted() {
        this.getServers();

        this.resetFormErrors();

        Promise.resolve()
            .then(this.syncUser)
            .then(this.displayMollieMessage)
            .then(this.fetchMollieUrl);
    },
    methods: {
        ...mapActions({
            sendOrder: 'order/send',
            setUser: 'user/setUser',
            setCustomer: 'order/setCustomer',
            resetDomains: 'domains/reset',
            addPageText: 'pageTexts/add',
            storeFormErrors: 'order/storeFormErrors',
            resetFormErrors: 'order/resetFormErrors'
        }),
        /**
         * Get the subscriptions.
         */
        getServers() {
            ServersApi.get().then(this.finishLoading);
        },
        /**
         * Sync the currently logged in user if we have been redirected from Mollie.
         *
         * @returns {Promise}
         */
        syncUser() {
            if (this.isRedirectedFromMollie) {
                return UsersApi.currentUser();
            } else {
                return Promise.resolve();
            }
        },
        /**
         * Display a message if we have been redirected from Mollie.
         *
         * @returns {Promise}
         */
        displayMollieMessage() {
            if (this.isRedirectedFromMollie) {
                if (this.currentCustomer && this.currentCustomer.isValidated) {
                    EventBus.$emit('notification', {
                        type: 'success',
                        message: trans('payment.success')
                    });
                } else {
                    EventBus.$emit('notification', {
                        type: 'error',
                        message: trans('payment.error')
                    });
                }
            }

            return Promise.resolve();
        },
        /**
         * Fetch the mollie url if the customer is not yet validated.
         *
         * @returns {Promise}
         */
        fetchMollieUrl() {
            if (!this.currentCustomer) {
                return Promise.resolve();
            }

            if (!this.currentCustomer.isValidated) {
                return PaymentApi.checkout()
                    .then(response => {
                        this.mollieUrl = response.data;
                    })
                    .catch(error => Promise.reject(error));
            } else {
                return Promise.resolve();
            }
        },

        /**
         * Submit the order to the backend.
         *
         * TODO: RELAPP-165 | Poll on the confirmation page for the status.
         * Or save the response (in e.g. a vuex store),
         * so you can show the response on the confirmation page.
         *
         * @return {boolean|void}
         */
        submitOrder() {
            if (!this.validateOverviewOptions()) {
                return false;
            }

            this.startLoading();

            this.sendOrder()
                .then(result => {
                    this.addPageText(result.confirmationText);
                    this.resetDomains();
                    this.$router.push({ name: 'orderFullConfirmation' });
                    // this is done asynchronously so the tracking has no effect on the completion of an order.
                    Promise.resolve().then(() => {
                        this.trackPlacedOrder(result.transactionId, result.productsOrdered.subscriptions);
                    });
                })
                .catch(error => {
                    this.storeFormErrors(error.data.errors);
                    this.showValidationNotification(error, trans('message.error.order'));
                })
                .then(this.finishLoading);
        }
    }
};
</script>
